/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace component-selector */

import {Injectable}         from '@angular/core';

import {
  Observable,
  Subject
}                                     from 'rxjs';

import { Action, ActionType         } from '@/models/action';

import { Logger                     } from '@/util/logger';

import { AdminAction                } from '@/actions/admin.action';
import { AdminUsersAction           } from '@/actions/admin.users.action';
import { LogoutAction               } from '@/actions/logout.action';
import { ProfileAction              } from '@/actions/profile.action';
import { Router                     } from '@angular/router';
import {ProjectNewAction}           from '@/actions/project.new.action';
import {ProjectCreateFromAction}    from '@/actions/project.create.from.action';
import {ProjectOpenAction}          from '@/actions/project.open.action';
import {ProjectCloseAction}         from '@/actions/project.close.action';
import {ProjectDeleteAction}        from '@/actions/project.delete.action';
import {ProjectRenameAction}        from '@/actions/project.rename.action';
import {WindowProjectAction}        from '@/actions/window.project.action';
import {WindowFileBrowserAction}    from '@/actions/window.file.browser.action';
import {ScenarioBrowseAction}       from '@/actions/scenario.browse.action';
import { ConnectionViewerAction }   from '@/actions/connection.viewer.action';
import {StringLineViewAction}       from '@/actions/string.line.view.action';
import {WorkBloxEditAction}         from '@/actions/work.blox.edit.action';
import {DiversionEditAction}        from '@/actions/diversion.edit.action';
import {TSOEditAction}              from '@/actions/t.s.o.edit.action';
import {WindowSearchAction}         from '@/actions/window.search.action';
import {WindowPropertiesAction}     from '@/actions/window.properties.action';
import {WindowJobsAction}           from '@/actions/window.jobs.action';
import {WindowSaveLayoutAction}     from '@/actions/window.save.layout.action';
import {WindowRestoreDefaultAction} from '@/actions/window.restore.default.action';
import {HelpDocumentation}          from '@/actions/help.documentation';
import {HelpDemosAction}            from '@/actions/help.demos.action';
import {HelpFaqAction}              from '@/actions/help.faq.action';
import {HelpAddUserAction}          from '@/actions/help.add.user.action';
import {HelpAboutAction}            from '@/actions/help.about.action';
import {JSONEditAction}             from '@/actions/json.edit.action';
import {TextEditAction}             from '@/actions/text.edit.action';


@Injectable({
  providedIn: 'root'
})

/**
 *
 * @class ActionService
 *
 * from anywhere in our application we can fetch and modify GUI Action items (commands),
 * and when we save modifications any watchers are notified of the change (so they can update
 * their presentation of the action).  The actions might be used in toolbars, buttons, menus
 * etc., but there is only ever one instance of a given action.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2020
 *
 */
export class ActionService {

  /** the actions we're managing */
  private actions: Map<ActionType, Action> = new Map<ActionType, Action>();

  /** when we "save", we push through an observable to "publish" the change */
  private subject   = new Subject<Action>();

  /** standard constructor */
  constructor(private router:  Router) {

    /* fill the pool with all possible actions */

    Logger.debug('[ActionService] creating actions...');

    /* setting menu (right side) */

    this.add(new AdminAction(this.router));
    this.add(new AdminUsersAction(this.router));
    this.add(new ProfileAction(this.router));
    this.add(new LogoutAction());

    /* project / file menu */

    this.add(new ProjectNewAction());
    this.add(new ProjectCreateFromAction());
    this.add(new ProjectOpenAction());
    this.add(new ProjectCloseAction());
    this.add(new ProjectDeleteAction());
    this.add(new ProjectRenameAction());

    /* window management menu */

    this.add(new WindowProjectAction());
    this.add(new ScenarioBrowseAction());
    this.add(new StringLineViewAction());
    this.add(new ConnectionViewerAction());
    this.add(new WorkBloxEditAction());
    this.add(new DiversionEditAction());
    this.add(new TSOEditAction());
    this.add(new WindowFileBrowserAction());
    this.add(new WindowSearchAction());
    this.add(new TextEditAction());
    this.add(new JSONEditAction());
    this.add(new WindowPropertiesAction());
    this.add(new WindowJobsAction());
    this.add(new WindowSaveLayoutAction());
    this.add(new WindowRestoreDefaultAction());

    /* help menu */

    this.add(new HelpDocumentation());
    this.add(new HelpDemosAction());
    this.add(new HelpFaqAction());
    this.add(new HelpAddUserAction());
    this.add(new HelpAboutAction());

    Logger.debug('[ActionService] ready.');
  }

  /* subscribe to any changes in actions */
  onChange(): Observable<Action> {

    return this.subject.asObservable();
  }

  /** add a new action, we don't publish addition/creation, only later changes. */
  add(action: Action) {

    /* overwrite if it's already there  */

    this.actions.set(action.type, action);
  }

  /** fetch an action */
  action(type: ActionType) : Action {

    if (!this.actions.has(type)) {

      /* this action isn't managed */

      return null;
    }

    /* pas it back */

    return this.actions.get(type);
  }

  /** save an action back the service, so the change can be published */
  save(action: Action) {

    if (!this.actions.has(action.type)) {

      /* this action isn't managed */

      return ;
    }

    /* save it */

    this.actions.set(action.type, action);

    /* publish change */

    this.subject.next(action);
  }

}
